import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  IndexTable,
  Badge,
  Thumbnail,
  EmptyState,
  SkeletonBodyText,
} from '@shopify/polaris';
import { authenticatedFetch } from '../utils/fetch';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProducts() {
      try {
        const response = await authenticatedFetch('/api/products');
        if (response.ok) {
          const data = await response.json();
          setProducts(data.products || []);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <Page title="Products" backAction={{ url: '/' }}>
        <Card>
          <SkeletonBodyText lines={5} />
        </Card>
      </Page>
    );
  }

  if (products.length === 0) {
    return (
      <Page title="Products" backAction={{ url: '/' }}>
        <Card>
          <EmptyState
            heading="No products synced yet"
            action={{
              content: 'Open ApparelHub',
              url: 'https://apparelhub.ai',
              external: true,
            }}
            image="https://cdn.shopify.com/shopifycloud/web/assets/v1/vite/client/en/assets/empty-state-BhfLDv1L.svg"
          >
            <p>
              Create and sync products from the ApparelHub platform. They will
              appear here once synced to your Shopify store.
            </p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  const rowMarkup = products.map(
    ({ name, status, thumbnail, synced_at }, index) => (
      <IndexTable.Row id={name} key={index} position={index}>
        <IndexTable.Cell>
          {thumbnail ? (
            <Thumbnail source={thumbnail} alt={name} size="small" />
          ) : (
            <Thumbnail source="" alt={name} size="small" />
          )}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="bold">
            {name}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Badge
            tone={
              status === 'synced' || status === 'Synced'
                ? 'success'
                : status === 'pending' || status === 'Pending'
                  ? 'attention'
                  : 'critical'
            }
          >
            {status}
          </Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {synced_at ? new Date(synced_at).toLocaleDateString() : ''}
        </IndexTable.Cell>
      </IndexTable.Row>
    ),
  );

  return (
    <Page title="Products" backAction={{ url: '/' }}>
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <IndexTable
              itemCount={products.length}
              headings={[
                { title: '' },
                { title: 'Product' },
                { title: 'Sync Status' },
                { title: 'Last Synced' },
              ]}
              selectable={false}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
